import {
  defaultLocale,
  getMessagesForLocale,
  isSupportedLocale,
  type PortalLocale,
} from "@/lib/i18n/portal";
import { interpolate, type TranslationValues } from "@/lib/i18n/intl";

/**
 * Translate outside of React. The intl provider mirrors the active locale onto
 * `<html lang>`, so non-React callers (e.g. the React Query client's global
 * error handler) can resolve messages without the `useTranslations` hook.
 *
 * `key` is a dotted path (the same string the `keys` object yields, e.g.
 * `keys.errors.generic === "errors.generic"`). Falls back to the key itself if
 * the message is missing.
 */
function currentLocale(): PortalLocale {
  const lang =
    typeof document !== "undefined" ? document.documentElement.lang : "";
  return isSupportedLocale(lang) ? lang : defaultLocale;
}

export function translateStandalone(
  key: string,
  values?: TranslationValues,
): string {
  const locale = currentLocale();
  const messages = getMessagesForLocale(locale) as Record<string, unknown>;
  const value = key.split(".").reduce<unknown>((current, segment) => {
    if (!current || typeof current !== "object") return undefined;
    return (current as Record<string, unknown>)[segment];
  }, messages);
  if (typeof value !== "string") return key;
  // Same placeholder/plural handling the React `t` uses, so a message reads the
  // same whichever side of the boundary resolves it.
  return interpolate(value, values, locale);
}
